import { useEffect } from "react";
import type { PortfolioImage } from "@/features/portfolio-items/types";
import { Button } from "@/components/ui/Button";
import { SafeImage } from "@/components/ui/SafeImage";

interface ImageLightboxProps {
  images: PortfolioImage[];
  activeIndex: number;
  title: string;
  onClose: () => void;
  onChange: (index: number) => void;
}

export function ImageLightbox({ images, activeIndex, title, onClose, onChange }: ImageLightboxProps) {
  const image = images[activeIndex];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onChange(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
      if (event.key === "ArrowRight") onChange(activeIndex === images.length - 1 ? 0 : activeIndex + 1);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, images.length, onChange, onClose]);

  if (!image) return null;

  const previous = () => onChange(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
  const next = () => onChange(activeIndex === images.length - 1 ? 0 : activeIndex + 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 px-3 py-4 backdrop-blur-sm sm:px-4 sm:py-6" role="dialog" aria-modal="true" aria-label={`${title} image gallery`}>
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 w-full max-w-6xl">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="break-words text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100 sm:text-sm sm:tracking-[0.22em]">
            {title} / {activeIndex + 1} of {images.length}
          </p>
          <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="overflow-hidden rounded-lg border border-cyan-300/30 bg-black shadow-neon">
          <SafeImage src={image.url} alt={image.alt || title} fallbackLabel="Image unavailable" className="max-h-[76vh] w-full object-contain" />
        </div>
        {images.length > 1 ? (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:flex sm:justify-center">
            <Button variant="secondary" onClick={previous}>
              Previous
            </Button>
            <Button variant="secondary" onClick={next}>
              Next
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
